import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";

const features = [
  { icon: "ri-movie-2-line", title: "海量影视索引", desc: "覆盖电影与电视剧，按题材、地区、年份多维分类。" },
  { icon: "ri-search-line", title: "快捷搜索", desc: "输入关键词即可同时检索影视与资讯内容。" },
  { icon: "ri-heart-3-line", title: "本地收藏", desc: "一键收藏喜欢的作品，状态保存在本机浏览器。" },
  { icon: "ri-newspaper-line", title: "资讯追踪", desc: "电影新闻、剧集动态与行业观察，及时更新。" },
];

const guides = [
  { step: "01", title: "浏览与搜索", desc: "从首页或导航进入电影、电视剧分类，也可直接搜索片名与题材。" },
  { step: "02", title: "筛选与查看", desc: "使用题材、地区、年份筛选并排序，点击卡片进入详情页。" },
  { step: "03", title: "收藏与分享", desc: "在详情页收藏感兴趣的内容，或复制链接分享给朋友。" },
];

const contacts = [
  { icon: "ri-mail-line", label: "邮箱", value: "contact@modou-jingpin.com" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "麻豆精品影视" },
  { icon: "ri-weibo-line", label: "微博", value: "@麻豆精品" },
];

export default function About() {
  const [copied, setCopied] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  useSEO({
    title: "关于我们 - 麻豆精品",
    description:
      "了解麻豆精品的网站定位、使用说明与联系方式。我们致力于为影视爱好者提供最新影视内容索引与专业资讯。",
    keywords: "麻豆精品,关于我们,影视平台,联系方式",
  });

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const copyContact = async (label: string, value: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const input = document.createElement("input");
        input.value = value;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setCopied(label);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setCopied(null), 2400);
    } catch {
      setCopied(null);
    }
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
      <section className="overflow-hidden rounded-lg border border-background-200 bg-gradient-to-br from-primary-800 to-primary-950 px-6 py-12 md:px-12 md:py-16">
        <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
          <i className="ri-information-line" aria-hidden="true"></i>
          关于麻豆精品
        </span>
        <h1 className="mt-5 max-w-3xl font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
          为影视爱好者打造的<br className="hidden md:block" />
          内容与资讯平台
        </h1>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-primary-100 md:text-base">
          麻豆精品致力于为电影及电视剧爱好者打造一个丰富全面的影视内容平台，提供最新影片的索引参考与影视新闻资讯。
          我们希望帮助每一位观众更快地找到想看的作品，并及时了解影视行业的最新动态。
        </p>
      </section>

      <section className="mt-12">
        <h2 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
          <span className="h-2 w-2 rounded-full bg-accent-500"></span>
          我们能为你做什么
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                <i className={`${feature.icon} text-2xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                {feature.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
          <span className="h-2 w-2 rounded-full bg-accent-500"></span>
          使用说明
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
          {guides.map((guide) => (
            <div key={guide.step} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="font-heading text-3xl font-extrabold text-accent-500">
                {guide.step}
              </span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                {guide.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{guide.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
          <span className="h-2 w-2 rounded-full bg-accent-500"></span>
          联系方式
        </h2>
        <p className="mt-3 text-sm text-foreground-600">
          如果你有内容建议、合作意向或问题反馈，欢迎通过以下方式联系我们。
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3 md:gap-5">
          {contacts.map((contact) => (
            <div
              key={contact.label}
              className="flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className={contact.icon} aria-hidden="true"></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">{contact.label}</p>
                  <p className="truncate text-sm font-medium text-foreground-900">{contact.value}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => copyContact(contact.label, contact.value)}
                aria-label={`复制${contact.label}`}
                className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-600"
              >
                <i
                  className={copied === contact.label ? "ri-check-line" : "ri-file-copy-line"}
                  aria-hidden="true"
                ></i>
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <h2 className="font-heading text-lg font-bold text-foreground-950">内容声明</h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          本站为影视内容演示站点，页面中展示的影片信息、资讯内容均为演示数据，播放、推荐等功能均为前端演示，
          不含真实片源与第三方接口。所有影视作品版权归原权利人所有，请通过正规渠道观看正版内容。
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/category/movie"
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-compass-3-line" aria-hidden="true"></i>
            浏览影视分类
          </Link>
          <Link
            to="/news"
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-200"
          >
            <i className="ri-newspaper-line" aria-hidden="true"></i>
            查看影视资讯
          </Link>
        </div>
      </section>

      {copied && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 shadow-lg">
          联系方式已复制
        </div>
      )}
    </div>
  );
}